import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const OpenOrdersTable = ({ 
  orders, 
  selectedOrders, 
  onSelectOrder, 
  onSelectAll, 
  onSort, 
  sortConfig,
  onReorder 
}) => {
  const [cancellingOrders, setCancellingOrders] = useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-warning bg-warning-50';
      case 'partial': return 'text-primary bg-primary-50';
      case 'executing': return 'text-accent bg-accent-50';
      default: return 'text-text-secondary bg-secondary-50';
    }
  };

  const getOrderTypeColor = (type) => {
    switch (type) {
      case 'market': return 'text-accent bg-accent-50';
      case 'limit': return 'text-primary bg-primary-50';
      case 'stop-loss': return 'text-error bg-error-50';
      case 'stop-limit': return 'text-warning bg-warning-50';
      case 'trailing-stop': return 'text-secondary bg-secondary-100';
      default: return 'text-text-secondary bg-secondary-50';
    }
  };

  const getSideColor = (side) => {
    return side === 'buy' ? 'text-success' : 'text-error';
  };

  const handleCancelOrder = async (orderId) => {
    setCancellingOrders(prev => [...prev, orderId]);
    
    // Mock API call
    setTimeout(() => {
      setCancellingOrders(prev => prev.filter(id => id !== orderId));
      console.log('Order cancelled:', orderId);
    }, 1000);
  };

  const handleModifyOrder = (order) => {
    console.log('Modify order:', order);
  };

  const formatProgress = (filled, total) => {
    const percentage = (filled / total) * 100;
    return { percentage, filled, total };
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => onSort(column)}
      className="flex items-center space-x-1 text-left font-body-medium text-sm text-text-secondary hover:text-text-primary transition-colors duration-trading-fast"
    >
      <span>{children}</span>
      <div className="flex flex-col">
        <Icon 
          name="ChevronUp" 
          size={12} 
          className={sortConfig.key === column && sortConfig.direction === 'asc' ? 'text-primary' : 'text-text-secondary'} 
        />
        <Icon 
          name="ChevronDown" 
          size={12} 
          className={sortConfig.key === column && sortConfig.direction === 'desc' ? 'text-primary' : 'text-text-secondary'} 
        />
      </div>
    </button>
  );

  if (orders.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary-50 border-b border-border">
          <tr>
            <th className="px-6 py-4 text-left">
              <input
                type="checkbox"
                checked={selectedOrders.length === orders.length}
                onChange={onSelectAll}
                className="rounded border-border focus:ring-primary"
              />
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="timestamp">Time</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="pair">Pair</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="type">Type</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="side">Side</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="quantity">Quantity</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="price">Price</SortButton>
            </th>
            <th className="px-6 py-4 text-left">Progress</th>
            <th className="px-6 py-4 text-left">
              <SortButton column="status">Status</SortButton>
            </th>
            <th className="px-6 py-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {orders.map((order) => {
            const progress = formatProgress(order.filled, order.quantity);
            const isCancelling = cancellingOrders.includes(order.id);
            
            return (
              <tr key={order.id} className="hover:bg-secondary-50 transition-colors duration-trading-fast">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => onSelectOrder(order.id)}
                    className="rounded border-border focus:ring-primary"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <p className="font-data text-text-primary">
                      {order.timestamp.toLocaleTimeString()}
                    </p>
                    <p className="text-text-secondary">
                      {order.timestamp.toLocaleDateString()}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="font-data font-body-medium text-sm text-text-primary">
                    {order.pair}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-caption-medium ${getOrderTypeColor(order.type)}`}>
                    {order.type.replace('-', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`font-body-medium text-sm ${getSideColor(order.side)}`}>
                    {order.side.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="font-data text-sm text-text-primary">
                    {order.quantity.toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <p className="font-data text-text-primary">
                      ${order.price.toLocaleString()}
                    </p>
                    {order.stopPrice && (
                      <p className="text-text-secondary text-xs">
                        Stop: ${order.stopPrice.toLocaleString()}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-text-secondary">
                        {progress.filled.toLocaleString()} / {progress.total.toLocaleString()}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {progress.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-secondary-100 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-trading-slow"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-caption-medium ${getStatusColor(order.status)}`}>
                    {order.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleModifyOrder(order)}
                      className="p-1 text-text-secondary hover:text-primary transition-colors duration-trading-fast"
                      title="Modify Order"
                    >
                      <Icon name="Edit2" size={16} />
                    </button>
                    <button
                      onClick={() => onReorder(order)}
                      className="p-1 text-text-secondary hover:text-accent transition-colors duration-trading-fast"
                      title="Reorder"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                    <button
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={isCancelling}
                      className="p-1 text-text-secondary hover:text-error transition-colors duration-trading-fast disabled:opacity-50"
                      title="Cancel Order"
                    >
                      {isCancelling ? (
                        <Icon name="Loader2" size={16} className="animate-spin" />
                      ) : (
                        <Icon name="X" size={16} />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OpenOrdersTable;